import { createSlice } from "@reduxjs/toolkit";
import { initialState } from "../initialState.js";
import {
  createPlaces,
  createRacks,
  createSection,
  createShelves,
  createWarehouse,
  getAllWarehousesWithDetails,
  updateWarehouse,
  deleteWarehouse,
  deleteSection,
  deleteRack,
  deleteShelf,
  deletePlace,
  updateSection,
  updateRack,
  updateShelf,
  updatePlace,
  getAllWarehousesWithAllParts,
  addPartsToWarehouse,
  updatePart,
  getPartsUploadHistory,
} from "./operations.js";

const handlePending = state => {
  state.isLoading = true;
  state.error = null;
};

const handleRejected = (state, action) => {
  state.isLoading = false;
  state.error = action.payload;
};

const updateNodeInTree = (warehouses, payload, type) => {
  if (type === "section") {
    const warehouse = warehouses.find(w => w.id === payload.warehouse_id);
    const section = warehouse?.sections?.find(s => s.id === payload.id);

    if (section) Object.assign(section, payload);
  }

  if (type === "rack") {
    for (const warehouse of warehouses) {
      const section = warehouse.sections?.find(
        s => s.id === payload.section_id
      );

      const rack = section?.racks?.find(r => r.id === payload.id);

      if (rack) Object.assign(rack, payload);
    }
  }

  if (type === "shelf") {
    for (const warehouse of warehouses) {
      for (const section of warehouse.sections || []) {
        const rack = section.racks?.find(r => r.id === payload.rack_id);
        const shelf = rack?.shelves?.find(s => s.id === payload.id);

        if (shelf) Object.assign(shelf, payload);
      }
    }
  }

  if (type === "place") {
    for (const warehouse of warehouses) {
      for (const section of warehouse.sections || []) {
        for (const rack of section.racks || []) {
          const shelf = rack.shelves?.find(s => s.id === payload.shelf_id);
          const place = shelf?.places?.find(p => p.id === payload.id);

          if (place) Object.assign(place, payload);
        }
      }
    }
  }
};

const warehouseSlice = createSlice({
  name: "warehouse",
  initialState: initialState.warehouse,
  // selectedWarehouse: null,
  reducers: {
    // setSelectedWarehouse: (state, action) => {
    //   state.selectedWarehouse = action.payload;
    // }
  },
  extraReducers: builder =>
    builder
      // Get all warehouses with details +
      .addCase(getAllWarehousesWithDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllWarehousesWithDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.warehouses = action.payload; // Зберігаємо всі склади з повною структурою
      })
      .addCase(getAllWarehousesWithDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Get all warehouses with all parts +
      .addCase(getAllWarehousesWithAllParts.pending, state => {
        state.warehousesWithPartsLoading = true;
        state.error = null;
      })
      .addCase(getAllWarehousesWithAllParts.fulfilled, (state, action) => {
        state.warehousesWithPartsLoading = false;
        state.warehousesWithParts = action.payload;
      })
      .addCase(getAllWarehousesWithAllParts.rejected, (state, action) => {
        state.warehousesWithPartsLoading = false;
        state.error = action.payload;
      })

      // Delete warehouse +
      .addCase(deleteWarehouse.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteWarehouse.fulfilled, (state, action) => {
        state.isLoading = false;
        state.warehouses = state.warehouses.filter(
          warehouse => warehouse.id !== action.meta.arg
        );
      })
      .addCase(deleteWarehouse.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete section +
      .addCase(deleteSection.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSection.fulfilled, (state, action) => {
        state.isLoading = false;

        const sectionId = action.meta.arg;

        state.warehouses.forEach(warehouse => {
          warehouse.sections = warehouse.sections?.filter(
            section => section.id !== sectionId
          );
        });
      })
      .addCase(deleteSection.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete rack +
      .addCase(deleteRack.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRack.fulfilled, (state, action) => {
        state.isLoading = false;

        const rackId = action.meta.arg;

        state.warehouses.forEach(warehouse => {
          warehouse.sections?.forEach(section => {
            section.racks = section.racks?.filter(rack => rack.id !== rackId);
          });
        });
      })
      .addCase(deleteRack.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete shelf +
      .addCase(deleteShelf.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteShelf.fulfilled, (state, action) => {
        state.isLoading = false;

        const shelfId = action.meta.arg;

        state.warehouses.forEach(warehouse => {
          warehouse.sections?.forEach(section => {
            section.racks?.forEach(rack => {
              rack.shelves = rack.shelves?.filter(
                shelf => shelf.id !== shelfId
              );
            });
          });
        });
      })
      .addCase(deleteShelf.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Delete place +
      .addCase(deletePlace.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePlace.fulfilled, (state, action) => {
        state.isLoading = false;

        const placeId = action.meta.arg;

        state.warehouses.forEach(warehouse => {
          warehouse.sections?.forEach(section => {
            section.racks?.forEach(rack => {
              rack.shelves?.forEach(shelf => {
                shelf.places = shelf.places?.filter(
                  place => place.id !== placeId
                );
              });
            });
          });
        });
      })
      .addCase(deletePlace.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create Warehouse +
      .addCase(createWarehouse.pending, handlePending)
      .addCase(createWarehouse.fulfilled, (state, action) => {
        state.isLoading = false;
        // state.warehouses.push(action.payload);
      })
      .addCase(createWarehouse.rejected, handleRejected)

      // Update warehouse +
      .addCase(updateWarehouse.pending, handlePending)
      .addCase(updateWarehouse.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateWarehouse.rejected, handleRejected)

      // Create section+
      .addCase(createSection.pending, handlePending)
      .addCase(createSection.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(createSection.rejected, handleRejected)

      // Update section +
      .addCase(updateSection.pending, handlePending)

      .addCase(updateSection.fulfilled, (state, action) => {
        state.isLoading = false;
        updateNodeInTree(state.warehouses, action.payload, "section");
      })
      .addCase(updateSection.rejected, handleRejected)

      // Create racks +
      .addCase(createRacks.pending, handlePending)
      .addCase(createRacks.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(createRacks.rejected, handleRejected)

      // Update rack +

      .addCase(updateRack.pending, handlePending)

      .addCase(updateRack.fulfilled, (state, action) => {
        state.isLoading = false;
        updateNodeInTree(state.warehouses, action.payload, "rack");
      })
      .addCase(updateRack.rejected, handleRejected)

      // Create shelves +
      .addCase(createShelves.pending, handlePending)
      .addCase(createShelves.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(createShelves.rejected, handleRejected)

      // Update shelf +
      .addCase(updateShelf.pending, handlePending)
      .addCase(updateShelf.fulfilled, (state, action) => {
        state.isLoading = false;
        updateNodeInTree(state.warehouses, action.payload, "shelf");
      })
      .addCase(updateShelf.rejected, handleRejected)

      // Create places +
      .addCase(createPlaces.pending, handlePending)
      .addCase(createPlaces.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(createPlaces.rejected, handleRejected)

      // Update place +
      .addCase(updatePlace.pending, handlePending)
      .addCase(updatePlace.fulfilled, (state, action) => {
        state.isLoading = false;
        updateNodeInTree(state.warehouses, action.payload, "place");
      })
      .addCase(updatePlace.rejected, handleRejected)

      // ! PARTS

      // Add parts to warehouse +
      .addCase(addPartsToWarehouse.pending, handlePending)
      .addCase(addPartsToWarehouse.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(addPartsToWarehouse.rejected, handleRejected)

      // Update part +
      .addCase(updatePart.pending, handlePending)
      .addCase(updatePart.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updatePart.rejected, handleRejected)

      // get parts upload history +
      .addCase(getPartsUploadHistory.pending, state => {
        state.partsUploadHistoryLoading = true;
        state.error = null;
      })
      .addCase(getPartsUploadHistory.fulfilled, (state, action) => {
        state.partsUploadHistoryLoading = false;
        const { items = [], total = 0 } = action.payload;
        // offset 0 - свіжий список, інакше догружаємо наступну порцію
        state.partsUploadHistory =
          action.meta.arg?.offset > 0
            ? [...state.partsUploadHistory, ...items]
            : items;
        state.partsUploadHistoryTotal = total;
      })
      .addCase(getPartsUploadHistory.rejected, (state, action) => {
        state.partsUploadHistoryLoading = false;
        state.error = action.payload;
      })
});

export default warehouseSlice.reducer;
